import { useState, useEffect } from "react";
import axios from "axios";
import { DashboardLayout, adminNavItems } from "@/components/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { 
  User, 
  CheckCircle2, 
  XCircle,
  Clock,
  Star,
  Mail,
  Briefcase
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPromotions() {
  const { t } = useTranslation();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    fetchRequests();
  }, [statusFilter]);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/promotion-requests`, {
        params: statusFilter ? { status: statusFilter } : {}
      });
      setRequests(response.data);
    } catch (error) {
      console.error("Error fetching requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (requestId, action) => {
    try {
      await axios.put(`${API}/promotion-requests/${requestId}?action=${action}`);
      toast.success(`Request ${action}ed successfully`);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process request");
    }
  };

  const statusColors = {
    pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    approved: "bg-green-500/20 text-green-400 border-green-500/30",
    rejected: "bg-red-500/20 text-red-400 border-red-500/30",
  };

  return (
    <DashboardLayout navItems={adminNavItems} title="Promotion Requests">
      <div className="space-y-6" data-testid="admin-promotions">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-['Unbounded'] font-semibold text-white">
              Promotion Requests
            </h2>
            <p className="text-[#94A3B8]">Review worker promotion requests to Team Leader</p>
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-black/50 border border-white/10 text-white px-4 py-2 rounded-lg focus:border-[#23F7DD]"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-yellow-500/10 rounded-lg">
                  <Clock className="w-6 h-6 text-yellow-400" />
                </div>
                <div>
                  <p className="text-3xl font-['Unbounded'] font-bold text-yellow-400">
                    {requests.filter(r => r.status === "pending").length}
                  </p>
                  <p className="text-sm text-[#94A3B8]">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-500/10 rounded-lg">
                  <CheckCircle2 className="w-6 h-6 text-green-500" />
                </div>
                <div>
                  <p className="text-3xl font-['Unbounded'] font-bold text-green-500">
                    {requests.filter(r => r.status === "approved").length}
                  </p>
                  <p className="text-sm text-[#94A3B8]">Approved</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-red-500/10 rounded-lg">
                  <XCircle className="w-6 h-6 text-red-500" />
                </div>
                <div>
                  <p className="text-3xl font-['Unbounded'] font-bold text-red-500">
                    {requests.filter(r => r.status === "rejected").length}
                  </p>
                  <p className="text-sm text-[#94A3B8]">Rejected</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Requests List */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-10 h-10 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : requests.length === 0 ? (
              <div className="text-center py-12">
                <Star className="w-12 h-12 text-[#475569] mx-auto mb-4" />
                <p className="text-[#94A3B8]">No promotion requests yet</p>
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {requests.map((request) => (
                  <div
                    key={request.request_id}
                    className="p-6"
                    data-testid={`promotion-${request.request_id}`}
                  >
                    <div className="flex flex-col gap-4">
                      {/* Header Row */}
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                        {/* Requester Info */}
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                            <User className="w-6 h-6 text-purple-400" />
                          </div>
                          <div>
                            <p className="text-white font-medium text-lg">{request.user_name}</p>
                            <div className="flex items-center gap-2 text-sm text-[#94A3B8]">
                              <Mail className="w-4 h-4" />
                              {request.user_email}
                            </div>
                            <div className="flex items-center gap-2 mt-2">
                              <span className="px-2 py-0.5 bg-[#23F7DD]/10 text-[#23F7DD] rounded text-xs">
                                {request.current_role?.replace(/_/g, ' ')}
                              </span>
                              <span className="text-[#94A3B8]">→</span>
                              <span className="px-2 py-0.5 bg-purple-500/10 text-purple-400 rounded text-xs">
                                {request.requested_role?.replace(/_/g, ' ')}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-3">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusColors[request.status]}`}>
                            {request.status}
                          </span>

                          {request.status === "pending" && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleReview(request.request_id, "approve")}
                                className="bg-green-500 text-white hover:bg-green-600"
                              >
                                <CheckCircle2 className="w-4 h-4 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleReview(request.request_id, "reject")}
                                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Reject
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Details */}
                      <div className="ml-16 space-y-3">
                        {request.experience_years && (
                          <div className="flex items-center gap-2 text-sm">
                            <Briefcase className="w-4 h-4 text-[#23F7DD]" />
                            <span className="text-[#94A3B8]">Experience:</span>
                            <span className="text-white">{request.experience_years} years</span>
                          </div>
                        )}
                        
                        {request.reason && (
                          <div className="p-3 bg-white/5 rounded-lg">
                            <p className="text-sm text-[#94A3B8]">
                              <span className="text-white font-medium">Reason:</span> {request.reason}
                            </p>
                          </div>
                        )}

                        <p className="text-xs text-[#475569]">
                          Requested on {new Date(request.created_at).toLocaleDateString()} at{" "}
                          {new Date(request.created_at).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
